import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'

import { useTelemetrySource } from '../../lib/api/source.tsx'
import { AthenaError } from '../../lib/api/client.ts'
import { formatDayClock, formatClock } from '../../lib/format.ts'
import { agentHref, pivotHref } from '../../lib/logs/investigation.ts'
import {
  actionLabel,
  categoryFromFacets,
  categoryPresentation,
  directionLabel,
  integrityPresentation,
  moduleLabel,
  osLabel,
  outcomePresentation,
  registryTypeLabel,
  serviceStatePresentation,
  transportLabel,
} from '../../lib/logs/semantics.ts'
import { Link } from '../../lib/router.tsx'
import type {
  AmsiScan,
  DnsDetails,
  LogDetail,
  LogDetails,
  LogFacets,
  LogRow,
} from '../../lib/sse/types.ts'
import { useResource } from '../../lib/useResource.ts'
import { Badge } from '../primitives/Badge.tsx'
import { CodeBlock } from '../primitives/CodeBlock.tsx'
import { CopyValue, ShortId } from '../primitives/CopyValue.tsx'
import { Drawer } from '../primitives/Drawer.tsx'
import { AlertDetailsView } from '../detections/AlertDetailsView.tsx'
import { EventAiPanel } from '../ai/EventAiPanel.tsx'
import { ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { StatusDot } from '../primitives/StatusDot.tsx'

/**
 * A ficha de investigação de um evento (§6).
 *
 * Não é um dump de JSON. É a leitura estruturada que `facets` destrava (§19): o
 * cabeçalho diz em uma linha o QUE aconteceu e o RESULTADO; os blocos dizem onde,
 * quem e contra quem; cada valor relevante é um pivô que continua a investigação
 * (§7). O payload bruto fica por último, como registro forense — o operador não
 * precisa abri-lo para saber que houve uma conexão TCP de saída na porta 443.
 *
 * Campos nulos são OMITIDOS, nunca desenhados como "—" (§20). Um bloco inteiro
 * sem dado simplesmente não aparece — a ficha se adapta ao que o evento tem.
 */
export function EventDossier({ row, onClose }: { row: LogRow; onClose: () => void }) {
  const source = useTelemetrySource()
  const fetcher = useCallback((signal: AbortSignal) => source.logDetail(row, signal), [source, row])
  const { data, error, loading } = useResource<LogDetail>(fetcher)

  const gone = error instanceof AthenaError && error.code === 'log_not_found'
  const facets = data?.facets ?? null

  const category = categoryPresentation(
    facets === null
      ? categoryFromFacets(null, null, row.event_type)
      : categoryFromFacets(facets.event_category, facets.event_action, row.event_type),
  )
  const what = actionLabel(row.event_type, facets?.event_action)
  const outcome = outcomePresentation(facets?.event_outcome ?? null)

  return (
    <Drawer
      open
      onOpenChange={(open) => !open && onClose()}
      title={
        <span className="flex items-center gap-2">
          <CategoryTag category={category} />
          <span className="truncate">{what}</span>
        </span>
      }
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="tnum" title={`no endpoint · ${formatDayClock(row.event_time)} UTC`}>
            {formatDayClock(row.event_time)} UTC
          </span>
          <span aria-hidden>·</span>
          <span className="truncate">{row.hostname ?? row.endpoint_id}</span>
        </span>
      }
      meta={outcome !== null ? <Badge tone={outcome.tone} dot>{outcome.label}</Badge> : undefined}
      footer={<InvestigationActions row={row} facets={facets} onNavigate={onClose} />}
    >
      {gone ? (
        <div className="px-4 py-3">
          <Notice tone="neutral">
            Detalhe não disponível: o evento não existe mais ou saiu por retenção.
          </Notice>
        </div>
      ) : error !== null ? (
        <div className="px-4 py-3">
          <ErrorState error={error} />
        </div>
      ) : data === null ? (
        <LoadingRow label="Carregando a ficha…" />
      ) : (
        // A linha do banco: quem abre por `log_id` (a mesa da IA) só tem um resumo.
        <DossierBody row={data.row} detail={data} onNavigate={onClose} />
      )}

      {loading && data !== null && (
        <span className="block px-4 pb-2 text-micro text-fg-muted">atualizando…</span>
      )}
    </Drawer>
  )
}

/** O selo de categoria: código + tom, o mesmo da lista, para a leitura casar. */
function CategoryTag({ category }: { category: ReturnType<typeof categoryPresentation> }) {
  return (
    <Badge tone={category.tone} dot title={category.label}>
      {category.code}
    </Badge>
  )
}

/**
 * O corpo da ficha, já com o detalhe em mãos. Exportado para o render check
 * exercitar os blocos sem servidor.
 */
export function DossierBody({
  row,
  detail,
  onNavigate,
}: {
  row: LogRow
  detail: LogDetail
  onNavigate: () => void
}) {
  const { facets, details } = detail
  const logId = detail.row.id ?? row.id
  return (
    <>
      {details.alert !== null && <AlertDetailsView alert={details.alert} />}
      {logId !== null && <EventAiPanel logId={logId} onNavigate={onNavigate} />}
      <EventBlock row={row} facets={facets} />
      <HostBlock row={row} facets={facets} />
      <ProcessBlock facets={facets} details={details} onNavigate={onNavigate} />
      {details.amsi !== null && <AmsiBlock amsi={details.amsi} onNavigate={onNavigate} />}
      <UserBlock facets={facets} onNavigate={onNavigate} />
      <NetworkBlock facets={facets} onNavigate={onNavigate} />
      <DnsBlock
        question={details.dns?.question_name ?? facets.dns_question}
        dns={details.dns}
        onNavigate={onNavigate}
      />
      <FileBlock facets={facets} />
      <RegistryBlock facets={facets} details={details} />
      <ServiceBlock facets={facets} />
      <CorrelationBlock facets={facets} onNavigate={onNavigate} />
      <RawBlock detail={detail} row={row} />
    </>
  )
}

/* ── blocos ──────────────────────────────────────────────────────────────── */

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-b border-line-subtle px-4 py-3">
      <h3 className="mb-1.5 text-micro font-medium uppercase tracking-label text-fg-secondary">
        {title}
      </h3>
      {children}
    </section>
  )
}

/** Linha rótulo→valor. `children` nulo NÃO é chamado — quem chama gateia (§20). */
function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-x-3 py-0.5">
      <dt className="pt-px text-micro uppercase tracking-label text-fg-muted">{label}</dt>
      <dd className="min-w-0 text-sm break-words text-fg">{children}</dd>
    </div>
  )
}

function Mono({ children }: { children: ReactNode }) {
  return <span className="font-mono text-fg-secondary">{children}</span>
}

function EventBlock({ row, facets }: { row: LogRow; facets: LogFacets }) {
  const outcome = outcomePresentation(facets.event_outcome)
  const mod = moduleLabel(facets.event_module)
  const categories = facets.event_category !== null ? facets.event_category.join(', ') : null

  return (
    <Block title="Evento">
      <dl>
        <Line label="Ação">
          <Mono>{actionLabel(row.event_type, facets.event_action)}</Mono>
        </Line>
        {categories !== null && (
          <Line label="Categoria">
            <Mono>{categories}</Mono>
          </Line>
        )}
        {outcome !== null && (
          <Line label="Resultado">
            <Badge tone={outcome.tone} dot>
              {outcome.label}
            </Badge>
          </Line>
        )}
        {mod !== null && (
          <Line label="Módulo">
            {mod}
            {facets.event_provider !== null && (
              <span className="text-fg-muted"> · {facets.event_provider}</span>
            )}
          </Line>
        )}
        {mod === null && facets.event_provider !== null && (
          <Line label="Provider">{facets.event_provider}</Line>
        )}
        {row.native_id !== null && (
          <Line label="Event ID">
            <Mono>{row.native_id}</Mono>
          </Line>
        )}
        <Line label="Tipo">
          <Mono>{row.event_type}</Mono>
        </Line>
        {row.source !== null && (
          <Line label="Canal">
            <Mono>{row.source}</Mono>
          </Line>
        )}
        <Line label="No endpoint">
          <span className="tnum font-mono" title={`${formatDayClock(row.event_time)} UTC`}>
            {formatClock(row.event_time)} UTC
          </span>
        </Line>
        <Line label="Recebido">
          <span className="tnum font-mono" title={`${formatDayClock(row.received_at)} UTC`}>
            {formatClock(row.received_at)} UTC
          </span>
        </Line>
      </dl>
    </Block>
  )
}

function HostBlock({ row, facets }: { row: LogRow; facets: LogFacets }) {
  const hostname = facets.host_hostname ?? row.hostname
  const os = osLabel(facets.host_os)

  return (
    <Block title="Host">
      <dl>
        {hostname !== null && (
          <Line label="Hostname">
            <Mono>{hostname}</Mono>
          </Line>
        )}
        <Line label="Empresa">
          {row.tenant_name ?? <span className="italic text-fg-muted">não resolvida</span>}
        </Line>
        <Line label="Endpoint">
          <ShortId id={row.endpoint_id} label="endpoint_id" />
        </Line>
        {os !== null && <Line label="SO">{os}</Line>}
      </dl>
    </Block>
  )
}

function ProcessBlock({
  facets,
  details,
  onNavigate,
}: {
  facets: LogFacets
  details: LogDetails
  onNavigate: () => void
}) {
  const has =
    details.process_args !== null ||
    details.process_exit_code !== null ||
    facets.process_creator_entity_id !== null ||
    facets.process_name !== null ||
    facets.process_executable !== null ||
    facets.process_pid !== null ||
    facets.process_entity_id !== null ||
    facets.process_command_line !== null ||
    facets.process_integrity !== null ||
    facets.process_parent_pid !== null ||
    facets.process_parent_entity_id !== null
  if (!has) return null

  const integrity = integrityPresentation(facets.process_integrity)

  return (
    <Block title="Processo">
      {facets.process_creator_entity_id !== null && (
        <SpoofAlert
          declaredPid={facets.process_parent_pid}
          creatorId={facets.process_creator_entity_id}
          creatorPid={facets.process_creator_pid}
          onNavigate={onNavigate}
        />
      )}
      <dl>
        {facets.process_name !== null && (
          <Line label="Nome">
            <Mono>{facets.process_name}</Mono>
          </Line>
        )}
        {facets.process_executable !== null && (
          <Line label="Executável">
            <Mono>{facets.process_executable}</Mono>
          </Line>
        )}
        {facets.process_pid !== null && (
          <Line label="PID">
            <Mono>{facets.process_pid}</Mono>
          </Line>
        )}
        {integrity !== null && (
          <Line label="Integridade">
            <Badge tone={integrity.tone} dot={integrity.tone !== 'neutral'}>
              {integrity.label}
            </Badge>
          </Line>
        )}
        {facets.process_command_line !== null && (
          <Line label="Linha de comando">
            <span className="font-mono text-fg-secondary">{facets.process_command_line}</span>
          </Line>
        )}
        {details.process_args !== null && (
          <Line label="Argumentos">
            <ArgList args={details.process_args} />
          </Line>
        )}
        {details.process_exit_code !== null && (
          <Line label="Código de saída">
            <Mono>{details.process_exit_code}</Mono>
          </Line>
        )}
        {facets.process_parent_pid !== null && (
          <Line label="PID pai">
            <Mono>{facets.process_parent_pid}</Mono>
          </Line>
        )}
        {facets.process_entity_id !== null && (
          <Line label="Process GUID">
            <div className="flex flex-col items-start gap-1">
              <CopyValue value={facets.process_entity_id} label="process_entity_id" />
              <div className="flex flex-wrap gap-1">
                <Pivot
                  href={pivotHref.process(facets.process_entity_id)}
                  onNavigate={onNavigate}
                  title="Árvore e linha do tempo deste processo"
                >
                  Timeline do processo
                </Pivot>
                <Pivot
                  href={pivotHref.processEvents(facets.process_entity_id)}
                  onNavigate={onNavigate}
                  title="Os eventos deste processo como filtro do histórico"
                >
                  No histórico
                </Pivot>
              </div>
            </div>
          </Line>
        )}
        {facets.process_parent_entity_id !== null && (
          <Line label="GUID pai">
            <div className="flex flex-col items-start gap-1">
              <CopyValue value={facets.process_parent_entity_id} label="process_parent_entity_id" />
              <Pivot
                href={pivotHref.process(facets.process_parent_entity_id)}
                onNavigate={onNavigate}
                title="Subir a árvore: o processo pai declarado"
              >
                Investigar processo pai
              </Pivot>
            </div>
          </Line>
        )}
      </dl>
    </Block>
  )
}

/**
 * PPID spoofing (§12.1): o pai DECLARADO não é quem criou o processo. Técnica
 * clássica para esconder um processo atrás de um pai legítimo (explorer.exe) —
 * por isso o alerta vem antes de qualquer outro campo do bloco.
 */
function SpoofAlert({
  declaredPid,
  creatorId,
  creatorPid,
  onNavigate,
}: {
  declaredPid: number | null
  creatorId: string
  creatorPid: number | null
  onNavigate: () => void
}) {
  return (
    <div role="alert" className="mb-2 border-l-2 border-crit px-2 py-1.5">
      <p className="text-sm font-medium text-crit">PPID spoofing</p>
      <p className="mt-0.5 text-sm leading-snug text-fg-secondary">
        O pai declarado{declaredPid !== null && <> (PID <Mono>{declaredPid}</Mono>)</>} não é quem
        criou este processo.
        {creatorPid !== null && (
          <>
            {' '}
            Criador real: PID <Mono>{creatorPid}</Mono>.
          </>
        )}
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-1">
        <CopyValue
          value={creatorId}
          display={creatorId.slice(0, 8)}
          label="process_creator_entity_id"
        />
        <Pivot
          href={pivotHref.process(creatorId)}
          onNavigate={onNavigate}
          title="Árvore e linha do tempo do criador real"
        >
          Investigar criador real
        </Pivot>
      </div>
    </div>
  )
}

/** argv do Linux, numerado a partir de 0 — `argv[0]` é o nome, como no kernel. */
function ArgList({ args }: { args: readonly string[] }) {
  return (
    <ol className="space-y-px font-mono text-fg-secondary">
      {args.map((arg, i) => (
        <li key={i} className="flex gap-2">
          <span className="tnum w-6 shrink-0 text-right text-fg-muted">{i}</span>
          <span className="min-w-0 break-all">
            {arg === '' ? <span className="italic text-fg-muted">(vazio)</span> : arg}
          </span>
        </li>
      ))}
    </ol>
  )
}

/**
 * O script que o AMSI inspecionou (§12.1). `content` é texto hostil
 * desofuscado e vai para o `CodeBlock`, que nunca o interpreta.
 */
function AmsiBlock({ amsi, onNavigate }: { amsi: AmsiScan; onNavigate: () => void }) {
  return (
    <Block title="Script inspecionado (AMSI)">
      <dl className="mb-2">
        {amsi.detected !== null && (
          <Line label="Veredito">
            {amsi.detected ? (
              <Badge tone="crit" dot>
                detectado
              </Badge>
            ) : (
              <Badge tone="neutral">não detectado</Badge>
            )}
            {amsi.scan_result !== null && (
              <span className="ml-2 font-mono text-micro text-fg-muted">
                AMSI_RESULT {amsi.scan_result}
              </span>
            )}
          </Line>
        )}
        {amsi.app_name !== null && (
          <Line label="Aplicação">
            <Mono>{amsi.app_name}</Mono>
          </Line>
        )}
        {amsi.content_name !== null && (
          <Line label="Conteúdo">
            <Mono>{amsi.content_name}</Mono>
          </Line>
        )}
        {amsi.hash !== null && (
          <Line label="Hash">
            <div className="flex flex-col items-start gap-1">
              <CopyValue value={amsi.hash} display={amsi.hash.slice(0, 16)} label="hash do script" />
              <Pivot
                href={pivotHref.relatedHash(amsi.hash)}
                onNavigate={onNavigate}
                title="Caçar este script: todo host que o executou"
              >
                Caçar hash
              </Pivot>
            </div>
          </Line>
        )}
      </dl>
      {amsi.content_truncated && (
        <div className="mb-2">
          <Notice tone="warn">
            Conteúdo truncado
            {amsi.content_size !== null && <> em {amsi.content_size} bytes</>}
            {amsi.original_size !== null && <> de {amsi.original_size} bytes originais</>}.
          </Notice>
        </div>
      )}
      {amsi.content !== null ? (
        <CodeBlock code={amsi.content} label="script inspecionado pelo AMSI" />
      ) : (
        <p className="text-sm text-fg-muted">O agente não enviou o conteúdo do script.</p>
      )}
    </Block>
  )
}

/**
 * A consulta DNS (§12.1). `dns_query` agora também vem do Windows, com o
 * processo — e com `event_outcome = failure` quando a resolução falha.
 */
function DnsBlock({
  question,
  dns,
  onNavigate,
}: {
  question: string | null
  dns: DnsDetails | null
  onNavigate: () => void
}) {
  if (question === null && dns === null) return null
  return (
    <Block title="DNS">
      <dl>
        {question !== null && (
          <Line label="Consulta">
            <div className="flex flex-col items-start gap-1">
              <Mono>{question}</Mono>
              <Pivot
                href={pivotHref.dnsQuestion(question)}
                onNavigate={onNavigate}
                title="Todo host que resolveu este domínio — na frota inteira"
              >
                Quem mais resolveu
              </Pivot>
            </div>
          </Line>
        )}
        {dns !== null && dns.question_type !== null && (
          <Line label="Tipo">
            <Mono>{dns.question_type}</Mono>
          </Line>
        )}
        {dns !== null && dns.response_code !== null && (
          <Line label="Resposta">
            <Mono>{dns.response_code}</Mono>
            {dns.response_code === 0 && <span className="text-fg-muted"> · sem erro</span>}
          </Line>
        )}
        {dns !== null && dns.answers !== null && (
          <Line label="Respostas">
            <ul className="space-y-px font-mono text-fg-secondary">
              {dns.answers.map((answer, i) => (
                <li key={i} className="break-all">
                  {answer}
                </li>
              ))}
            </ul>
          </Line>
        )}
      </dl>
    </Block>
  )
}

function UserBlock({ facets, onNavigate }: { facets: LogFacets; onNavigate: () => void }) {
  if (facets.user_name === null && facets.user_domain === null && facets.user_sid === null) {
    return null
  }

  const qualified =
    facets.user_domain !== null && facets.user_name !== null
      ? `${facets.user_domain}\\${facets.user_name}`
      : facets.user_name

  return (
    <Block title="Usuário">
      <dl>
        {facets.user_name !== null && (
          <Line label="Usuário">
            <div className="flex flex-col items-start gap-1">
              <Mono>
                {facets.user_domain !== null && (
                  <span className="text-fg-muted">{facets.user_domain}\</span>
                )}
                {facets.user_name}
              </Mono>
              {qualified !== null && (
                <Pivot
                  href={pivotHref.relatedUser(qualified)}
                  onNavigate={onNavigate}
                  title="O que este usuário fez na frota inteira"
                >
                  Investigar usuário
                </Pivot>
              )}
            </div>
          </Line>
        )}
        {facets.user_domain !== null && facets.user_name === null && (
          <Line label="Domínio">
            <Mono>{facets.user_domain}</Mono>
          </Line>
        )}
        {facets.user_sid !== null && (
          <Line label="SID">
            <Mono>{facets.user_sid}</Mono>
          </Line>
        )}
      </dl>
    </Block>
  )
}

function NetworkBlock({ facets, onNavigate }: { facets: LogFacets; onNavigate: () => void }) {
  const hasEndpoints = facets.source_ip !== null || facets.destination_ip !== null
  // A consulta DNS tem bloco próprio (DnsBlock), com tipo, código e respostas.
  if (!hasEndpoints) return null

  const transport = transportLabel(facets.network_transport)
  const direction = directionLabel(facets.network_direction)

  return (
    <Block title="Rede">
      {hasEndpoints && (
        <div className="mb-2 flex flex-col items-start gap-0.5 font-mono text-sm">
          {facets.source_ip !== null && (
            <Endpoint ip={facets.source_ip} port={facets.source_port} onNavigate={onNavigate} />
          )}
          {facets.source_ip !== null && facets.destination_ip !== null && (
            <span aria-hidden className="pl-2 text-fg-muted">
              ↓
            </span>
          )}
          {facets.destination_ip !== null && (
            <Endpoint
              ip={facets.destination_ip}
              port={facets.destination_port}
              onNavigate={onNavigate}
            />
          )}
        </div>
      )}
      {(transport !== null || direction !== null) && (
        <div className="mb-1 flex flex-wrap gap-1">
          {transport !== null && <Badge tone="neutral">{transport}</Badge>}
          {direction !== null && <Badge tone="neutral">{direction}</Badge>}
        </div>
      )}
    </Block>
  )
}

/** Um lado da conversa: IP clicável (pivô relatedIp) mais a porta. */
function Endpoint({
  ip,
  port,
  onNavigate,
}: {
  ip: string
  port: number | null
  onNavigate: () => void
}) {
  return (
    <span className="inline-flex items-baseline gap-0.5">
      <Link
        href={pivotHref.relatedIp(ip)}
        onClick={onNavigate}
        title="Quem falou com este IP — na frota inteira"
        className="text-fg underline decoration-line-strong underline-offset-2 hover:decoration-accent hover:text-accent"
      >
        {ip}
      </Link>
      {port !== null && <span className="text-fg-muted">:{port}</span>}
    </span>
  )
}

function FileBlock({ facets }: { facets: LogFacets }) {
  if (facets.file_path === null && facets.file_name === null && facets.file_extension === null) {
    return null
  }
  return (
    <Block title="Arquivo">
      <dl>
        {facets.file_name !== null && (
          <Line label="Nome">
            <span className="font-mono text-fg-secondary">{facets.file_name}</span>
          </Line>
        )}
        {facets.file_path !== null && (
          <Line label="Caminho">
            <span className="font-mono text-fg-secondary">{facets.file_path}</span>
          </Line>
        )}
        {facets.file_extension !== null && (
          <Line label="Extensão">
            <Mono>{facets.file_extension}</Mono>
          </Line>
        )}
      </dl>
    </Block>
  )
}

/**
 * O serviço (§telemetria Linux/Windows). Aparece nos eventos de `service_start`/
 * `service_stop`, e sempre que a faceta trouxer nome ou estado — o `stopped` de
 * um serviço de segurança é o que o analista repara. Campos nulos omitidos (§20).
 */
function ServiceBlock({ facets }: { facets: LogFacets }) {
  if (facets.service_name === null && facets.service_state === null) return null

  const state = serviceStatePresentation(facets.service_state)

  return (
    <Block title="Serviço">
      <dl>
        {facets.service_name !== null && (
          <Line label="Nome">
            <Mono>{facets.service_name}</Mono>
          </Line>
        )}
        {state !== null && (
          <Line label="Estado">
            <Badge tone={state.tone} dot={state.tone !== 'neutral'}>
              {state.label}
            </Badge>
          </Line>
        )}
      </dl>
    </Block>
  )
}

function RegistryBlock({ facets, details }: { facets: LogFacets; details: LogDetails }) {
  if (facets.registry_path === null && details.registry_data === null) return null
  const type = registryTypeLabel(details.registry_data_type)
  return (
    <Block title="Registro">
      <dl>
        {facets.registry_path !== null && (
          <Line label="Chave">
            <span className="font-mono text-fg-secondary">{facets.registry_path}</span>
          </Line>
        )}
        {details.registry_data !== null && (
          <Line label="Valor gravado">
            <span className="font-mono break-all text-fg-secondary">
              {details.registry_data === '' ? (
                <span className="italic text-fg-muted">(vazio)</span>
              ) : (
                details.registry_data
              )}
            </span>
          </Line>
        )}
        {(type !== null || details.registry_data_size !== null) && (
          <Line label="Tipo">
            {type !== null && <Mono>{type}</Mono>}
            {details.registry_data_size !== null && (
              <span className="text-fg-muted">
                {type !== null && ' · '}
                {details.registry_data_size} bytes
              </span>
            )}
          </Line>
        )}
      </dl>
    </Block>
  )
}

/**
 * Correlação: os pivôs de fan-out reunidos (§7, §16). É a diferença entre "ler
 * um evento" e "sair dele para o próximo" — cada valor abre a lista filtrada.
 */
function CorrelationBlock({ facets, onNavigate }: { facets: LogFacets; onNavigate: () => void }) {
  const hasHash = facets.related_hash !== null
  const hasIp = facets.related_ip !== null
  const hasUser = facets.related_user !== null
  if (!hasHash && !hasIp && !hasUser) return null

  return (
    <Block title="Investigação">
      <div className="space-y-2">
        {facets.related_hash !== null && (
          <PivotGroup label="Hashes (IOC)">
            {facets.related_hash.map((hash) => (
              <Pivot
                key={hash}
                href={pivotHref.relatedHash(hash)}
                onNavigate={onNavigate}
                title="Caçar este IOC: todo host que viu este binário"
              >
                <span className="max-w-[16rem] truncate">{hash}</span>
              </Pivot>
            ))}
          </PivotGroup>
        )}
        {facets.related_ip !== null && (
          <PivotGroup label="IPs">
            {facets.related_ip.map((ip) => (
              <Pivot
                key={ip}
                href={pivotHref.relatedIp(ip)}
                onNavigate={onNavigate}
                title="Quem falou com este IP"
              >
                {ip}
              </Pivot>
            ))}
          </PivotGroup>
        )}
        {facets.related_user !== null && (
          <PivotGroup label="Usuários">
            {facets.related_user.map((user) => (
              <Pivot
                key={user}
                href={pivotHref.relatedUser(user)}
                onNavigate={onNavigate}
                title="Atividade deste usuário na frota"
              >
                {user}
              </Pivot>
            ))}
          </PivotGroup>
        )}
      </div>
    </Block>
  )
}

function PivotGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="mb-1 block text-micro uppercase tracking-label text-fg-muted">{label}</span>
      <div className="flex flex-wrap gap-1">{children}</div>
    </div>
  )
}

/**
 * Um pivô: chip que abre a lista já filtrada. Fecha a ficha ao navegar, porque a
 * lista atrás dele muda para o resultado da investigação.
 */
function Pivot({
  href,
  onNavigate,
  title,
  children,
}: {
  href: string
  onNavigate: () => void
  title: string
  children: ReactNode
}) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      title={title}
      className="inline-flex items-center gap-1 border border-line-strong px-1.5 py-0.5 text-micro uppercase tracking-label text-fg-secondary hover:border-accent hover:text-accent"
    >
      <span aria-hidden>→</span>
      {children}
    </Link>
  )
}

/**
 * Ações de investigação no rodapé: a timeline do processo (§8) e o antes/depois
 * nesta máquina (§15). Aparecem só quando fazem sentido — antes/depois sempre,
 * porque a linha carrega o endpoint; timeline só com um Process GUID.
 */
function InvestigationActions({
  row,
  facets,
  onNavigate,
}: {
  row: LogRow
  facets: LogFacets | null
  onNavigate: () => void
}) {
  return (
    <>
      <Pivot
        href={agentHref(row.endpoint_id, { to: row.event_time })}
        onNavigate={onNavigate}
        title="Os eventos desta máquina até este instante"
      >
        Antes
      </Pivot>
      <Pivot
        href={agentHref(row.endpoint_id, { from: row.event_time })}
        onNavigate={onNavigate}
        title="Os eventos desta máquina a partir deste instante"
      >
        Depois
      </Pivot>
      {facets?.process_entity_id != null && (
        <Pivot
          href={pivotHref.process(facets.process_entity_id)}
          onNavigate={onNavigate}
          title="Árvore e linha do tempo deste processo"
        >
          Timeline do processo
        </Pivot>
      )}
    </>
  )
}

/**
 * O bruto — lineage e payload. Último e recolhido (§19): existe para o forense,
 * não para descobrir o que aconteceu. O `payload` é estrutura arbitrária do
 * agente e é renderizado como JSON, sem tentativa de tipar (§3.4).
 */
function RawBlock({ detail, row }: { detail: LogDetail; row: LogRow }) {
  const [open, setOpen] = useState(false)

  return (
    <section className="px-4 py-3">
      <div className="mb-1.5 flex items-center gap-2">
        <StatusDot tone="neutral" />
        <h3 className="text-micro font-medium uppercase tracking-label text-fg-secondary">
          Evento bruto
        </h3>
      </div>
      <dl className="mb-2 flex flex-wrap gap-x-6 gap-y-1">
        {detail.batch_id !== null && (
          <Line label="batch_id">
            <ShortId id={detail.batch_id} label="batch_id" />
          </Line>
        )}
        {detail.agent_version !== null && (
          <Line label="Agente">
            <Mono>{detail.agent_version}</Mono>
          </Line>
        )}
        {row.record_id !== null && (
          <Line label="record_id">
            <Mono>{row.record_id}</Mono>
          </Line>
        )}
      </dl>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="font-mono text-micro text-fg-muted hover:text-fg"
      >
        {open ? '▾ ocultar payload' : '▸ mostrar payload'}
      </button>
      {open && <PayloadView payload={detail.payload} />}
    </section>
  )
}

function PayloadView({ payload }: { payload: unknown }) {
  if (payload === null || payload === undefined) {
    return <p className="mt-1 text-sm text-fg-muted">Sem payload.</p>
  }

  let text: string
  try {
    text = JSON.stringify(payload, null, 2)
  } catch {
    text = String(payload)
  }

  return (
    <div className="mt-1 max-h-96 overflow-auto border border-line-subtle bg-surface">
      <pre className="min-w-0 px-2 py-1.5 font-mono text-micro leading-snug text-fg-secondary">
        {text}
      </pre>
    </div>
  )
}
